// useGetStaffForDate.ts
import { dateToApiFormat } from '@/helpers/dateHelper';
import {
  useGetDayShiftsQuery,
  useGetEmployeeNamesByIdsQuery,
  useGetMeQuery,
  useGetMyShiftsForDateRangeQuery,
  useGetPublishedRosteredStaffInDateRangeQuery
} from '@/services/gql/graphql.generated';
import { useMemo } from 'react';
import { useMyLocation } from '../useLocations';

export const useGetStaffForDate = (selectedDate: Date) => {
  const selectedLocationId = useMyLocation().id;

  const [{ data: myShiftsData }] = useGetMyShiftsForDateRangeQuery({
    variables: {
      range: {
        start: dateToApiFormat(selectedDate),
        end: dateToApiFormat(selectedDate)
      }
    },
    context: useMemo(() => ({ suspense: false }), []),
    requestPolicy: 'network-only'
  });

  const [{ data: rosterData }] = useGetPublishedRosteredStaffInDateRangeQuery({
    variables: {
      startDate: dateToApiFormat(selectedDate),
      endDate: dateToApiFormat(selectedDate),
      locationId: selectedLocationId
    },
    requestPolicy: 'cache-and-network'
  });

  const [{ data: userData }] = useGetMeQuery();

  const StaffIds = useMemo(
    () => (rosterData?.publishedRosteredStaffInDateRange ?? []).map(s => s.identityId),
    [rosterData, userData]
  );

  const [{ data: dayShiftsData }] = useGetDayShiftsQuery({
    variables: {
      dateAndIdentityIds: {
        date: dateToApiFormat(selectedDate),
        identityIds: StaffIds
      }
    },
    pause: StaffIds.length === 0,
    requestPolicy: 'cache-and-network'
  });

  const [{ data: staffNamesData }] = useGetEmployeeNamesByIdsQuery({
    variables: {
      ids: StaffIds
    },
    pause: StaffIds.length === 0,
    requestPolicy: 'cache-and-network'
  });

  const coworkersOnShift = useMemo(() => {
    const myShifts = myShiftsData?.myShiftsInDateRange?.shifts ?? [];

    return dayShiftsData?.dayShifts
      ?.map(day => ({
        ...day,
        shifts: day.shifts?.filter(otherShift => {
          if (otherShift.location?.id !== selectedLocationId) return false;

          return myShifts.some(myShift => {
            const myStart = new Date(myShift.start).getTime();
            const myEnd = new Date(myShift.end).getTime();
            const otherStart = new Date(otherShift.start).getTime();
            const otherEnd = new Date(otherShift.end).getTime();

            return otherStart < myEnd && otherEnd > myStart;
          });
        })
      }))
      .filter(d => d.shifts && d.shifts.length > 0);
  }, [dayShiftsData?.dayShifts, myShiftsData, selectedLocationId]);

  return { coworkersOnShift, otherStaff: staffNamesData?.employeeNamesByIds };
};
